package com.elan.edubridge.ui.member

import com.elan.edubridge.shared.domain.model.CommandFailure
import com.elan.edubridge.shared.domain.model.SessionState
import com.elan.edubridge.ui.shell.escapeHtml
import com.elan.edubridge.ui.shell.noticeBodyHtml
import com.elan.edubridge.ui.shell.formatTimestamp
import com.elan.edubridge.ui.shell.icon
import com.elan.edubridge.ui.shell.partialFailureHtml
import com.elan.edubridge.ui.shell.readErrorHtml
import com.elan.edubridge.ui.shell.wireRetry
import kotlinx.browser.document
import kotlinx.coroutines.MainScope
import kotlinx.coroutines.launch
import org.w3c.dom.HTMLButtonElement
import org.w3c.dom.HTMLElement

/** `/app/me/notices` — 공지 목록 + 빈 상태(가이드 §4.2 톤 재사용). 4탭 어디에도 속하지 않는
 * 하위 화면이라 [MemberShell]에는 `activeTab = null`을 넘긴다(가이드 §3.7: 공지는 홈 카드
 * 링크로만 진입하는 화면). */
object MyNoticesPage {
    private val scope = MainScope()

    /** 읽기 실패·부분 실패 양쪽의 "다시 시도" 버튼 id(#30). */
    private const val RETRY_ID = "member-notices-retry"

    private data class MemberNotice(
        val academyId: String,
        val title: String,
        val date: String,
        val content: String,
        val contentHtml: String?,
        val authorName: String?,
    )

    fun render(session: SessionState) {
        scope.launch {
            val context = loadMemberContext(session)
            renderList(context)
        }
    }

    private suspend fun renderList(context: MemberContext) {
        val academyIds = context.subjectAcademyIds()
        if (academyIds.isEmpty()) {
            MemberShell.render(
                context,
                activeTab = null,
                bodyHtml = emptyState("아직 연결된 학원이 없어요", "학원에서 보낸 초대 링크를 받으면 공지가 여기에 나타나요."),
            )
            return
        }

        // 학원별로 따로 읽으므로 일부만 실패할 수 있다(#30). 실패를 버리면 "아직 공지가 없어요"가
        // 되어 사용자가 학원이 공지를 안 쓴다고 결론짓는다 — 첫 실패를 남겨 화면이 구분하게 한다.
        var failure: CommandFailure? = null
        val notices = academyIds.flatMap { academyId ->
            val result = context.repository.noticesResult(academyId)
            if (failure == null) failure = result.failure
            result.items.map { notice ->
                MemberNotice(academyId, notice.title, notice.date, notice.content, notice.contentHtml, notice.authorName)
            }
        }.sortedByDescending { it.date }

        if (notices.isEmpty()) {
            val readFailure = failure
            MemberShell.render(
                context,
                activeTab = null,
                bodyHtml = if (readFailure != null) {
                    readErrorHtml(readFailure, RETRY_ID)
                } else {
                    emptyState("아직 공지가 없어요", "학원에서 새 공지를 올리면 여기에 나타나요.")
                },
            )
            wireRetry(RETRY_ID) { render(context.session) }
            return
        }

        val rows = notices.mapIndexed { index, notice ->
            val author = notice.authorName?.takeIf { it.isNotBlank() }?.let { " · ${it.escapeHtml()}" }.orEmpty()
            """
            <article class="eb-notice-item">
                ${icon("icon-notice", "md")}
                <div>
                    <button class="eb-button eb-button--link member-notice-open" type="button" data-notice-index="$index">${notice.title.escapeHtml()}</button>
                    <span class="eb-meta">${formatTimestamp(notice.date)}$author</span>
                    <p>${preview(notice.content).escapeHtml()}</p>
                </div>
            </article>
            """.trimIndent()
        }.joinToString("")

        // 목록은 왔지만 학원 하나가 빠졌을 수 있다 — 숨기지 않고 목록 위에 얹는다.
        val partialBanner = if (failure != null) partialFailureHtml(RETRY_ID) else ""
        MemberShell.render(context, activeTab = null, bodyHtml = """
            <h1 class="eb-page-title">공지</h1>
            $partialBanner
            <section class="eb-card">$rows</section>
        """.trimIndent())
        wireRetry(RETRY_ID) { render(context.session) }
        val buttons = document.querySelectorAll(".member-notice-open")
        for (index in 0 until buttons.length) {
            val button = buttons.item(index) as? HTMLElement ?: continue
            button.onclick = {
                button.getAttribute("data-notice-index")?.toIntOrNull()?.let(notices::getOrNull)?.let { openDetail(context, it) }
                null
            }
        }
    }

    private fun openDetail(context: MemberContext, notice: MemberNotice) {
        scope.launch {
            val author = notice.authorName?.takeIf { it.isNotBlank() }?.let { " · ${it.escapeHtml()}" }.orEmpty()
            MemberShell.render(context, activeTab = null, bodyHtml = """
                <div class="eb-page-header">
                    <button class="eb-button eb-button--ghost" id="member-notice-back" type="button">목록으로</button>
                </div>
                <article class="eb-card" aria-labelledby="member-notice-title">
                    <h1 class="eb-page-title" id="member-notice-title">${notice.title.escapeHtml()}</h1>
                    <p class="eb-meta">${formatTimestamp(notice.date)}$author</p>
                    ${noticeBodyHtml(notice.contentHtml, notice.content)}
                </article>
            """.trimIndent())
            (document.getElementById("member-notice-back") as? HTMLButtonElement)?.onclick = {
                render(context.session)
                null
            }
        }
    }

    private fun emptyState(title: String, body: String): String = """
        <div class="eb-empty-state">
            ${icon("icon-notice", "xl")}
            <h2>${title.escapeHtml()}</h2>
            <p>${body.escapeHtml()}</p>
        </div>
    """.trimIndent()

    private fun preview(content: String): String {
        val compact = content.replace(Regex("\\s+"), " ").trim()
        return if (compact.length > 120) "${compact.take(120)}…" else compact
    }

}
